/// <reference lib="dom" />

export class VideoTooLongError extends Error {
  constructor(message = 'VideoTooLongError') {
    super(message);
    this.name = 'VideoTooLongError';
  }
}

export class VideoCompressError extends Error {
  constructor(message = 'VideoCompressError') {
    super(message);
    this.name = 'VideoCompressError';
  }
}

export function webCodecsAvailable(): boolean {
  return typeof VideoEncoder !== 'undefined';
}

export async function readDurationSec(file: Blob): Promise<number> {
  const video = document.createElement('video');
  const src = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      video.addEventListener('loadedmetadata', () => resolve(), { once: true });
      video.addEventListener(
        'error',
        () => reject(new VideoCompressError()),
        { once: true },
      );
      video.src = src;
    });
    const duration = video.duration;
    if (typeof duration !== 'number' || !Number.isFinite(duration)) {
      throw new VideoCompressError();
    }
    return duration;
  } finally {
    URL.revokeObjectURL(src);
  }
}

async function convert(file: Blob, discardAudio: boolean) {
  const {
    ALL_FORMATS,
    BlobSource,
    BufferTarget,
    Conversion,
    Input,
    Mp4OutputFormat,
    Output,
  } = await import('mediabunny');

  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  const target = new BufferTarget();
  const output = new Output({
    format: new Mp4OutputFormat({ fastStart: 'in-memory' }),
    target,
  });
  const conversion = await Conversion.init({
    input,
    output,
    video: { codec: 'avc', bitrate: 1_500_000, width: 1280 },
    audio: discardAudio ? { discard: true } : { codec: 'aac', bitrate: 96_000 },
  });
  await conversion.execute();
  return new Blob([target.buffer!], { type: 'video/mp4' });
}

export async function compressExerciseVideo(
  file: Blob,
): Promise<{ blob: Blob; durationSec: number; muted: boolean }> {
  if (!webCodecsAvailable()) {
    throw new VideoCompressError();
  }

  const duration = await readDurationSec(file);
  if (duration > 90) {
    throw new VideoTooLongError();
  }

  let blob: Blob;
  let muted = false;
  try {
    blob = await convert(file, false);
  } catch {
    try {
      blob = await convert(file, true);
      muted = true;
    } catch {
      throw new VideoCompressError();
    }
  }

  const durationSec = Math.min(90, Math.max(1, Math.round(duration)));
  return { blob, durationSec, muted };
}
